// src/generators/dzial1_liczby_i_dzialania/temat1_dzialania/podtemat1_podzielnosc_liczb_calkowitych_types/DivisibilityFillDigitTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FiniteSetInput, { parseFiniteSetInput, finiteSetToLatex } from "../../../../components/inputs/FiniteSetInput";

// ===== helpers =====
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const rand = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

/** z wagami: [{v:3,w:2},{v:5,w:1}] */
function weightedPick(items) {
  const sum = items.reduce((s, it) => s + (it.w ?? 1), 0);
  let r = Math.random() * sum;
  for (const it of items) { r -= (it.w ?? 1); if (r <= 0) return it.v; }
  return items[items.length - 1].v;
}

function digitsToNumber(digs) {
  return parseInt(digs.join(""), 10);
}

function buildNumberLatex(digs, holeIdx) {
  // render w KaTeX; w miejscu braku dajemy \square
  const parts = digs.map((d, i) => (i === holeIdx ? "\\square" : String(d)));
  return parts.join("");
}

function makeDigits(len) {
  const out = [];
  for (let i = 0; i < len; i++) out.push(rand(0, 9));
  if (out[0] === 0) out[0] = rand(1, 9); // bez wiodącego zera
  return out;
}

/** cyfry 0..9, które po wstawieniu spełniają warunek podzielności */
function solveDigitsFor(divisor, digits, holeIdx) {
  const sols = [];
  for (let d = 0; d <= 9; d++) {
    if (holeIdx === 0 && d === 0) continue; // bez wiodącego zera
    const candidate = [...digits];
    candidate[holeIdx] = d;
    const n = digitsToNumber(candidate);
    if (n % divisor === 0) sols.push(d);
  }
  return Array.from(new Set(sols)).sort((a, b) => a - b);
}

// ===== pule dzielników =====
// Zad. 3 (BASIC): 2 rzadko; częściej {3,5,10,6}
const BASIC_POOL = [
  { v: 2,  w: 1 },  // rzadko
  { v: 3,  w: 4 },
  { v: 5,  w: 3 },
  { v: 10, w: 3 },
  { v: 6,  w: 3 },
];
// Zad. 4 (EXT): szersza pula z 4/8/9
const EXT_POOL = [
  { v: 2,  w: 2 },
  { v: 3,  w: 4 },
  { v: 4,  w: 4 },
  { v: 5,  w: 3 },
  { v: 6,  w: 3 },
  { v: 8,  w: 4 },
  { v: 9,  w: 3 },
  { v: 10, w: 3 },
];

/** wybór długości (zawsze ≥ 4; 8 preferuje 6-cyfrowe) */
function pickLength(divisor) {
  if (divisor === 8) {
    // {4,5,6,7} z biasem na 6
    return weightedPick([
      { v: 4, w: 1 },
      { v: 5, w: 2 },
      { v: 6, w: 4 },
      { v: 7, w: 2 },
    ]);
  }
  if (divisor === 4) {
    // {4,5,6} z lekkim biasem na 5-6
    return weightedPick([
      { v: 4, w: 2 },
      { v: 5, w: 3 },
      { v: 6, w: 3 },
    ]);
  }
  // pozostałe — {4,5,6}
  return weightedPick([
    { v: 4, w: 3 },
    { v: 5, w: 3 },
    { v: 6, w: 2 },
  ]);
}

/** wybór indeksu brakującej cyfry zgodnie z regułami */
function pickHoleIndex(divisor, len) {
  if (divisor === 10 || divisor === 5 || divisor === 2 || divisor === 6) {
    return len - 1; // ostatnia cyfra decyduje
  }
  if (divisor === 3 || divisor === 9) {
    // dowolna pozycja ≠ 0 (żeby nie kusiło wiodącym zerem)
    return rand(1, len - 1);
  }
  if (divisor === 4) {
    // jedna z dwóch ostatnich
    return len - (rand(1, 2));
  }
  if (divisor === 8) {
    // jedna z trzech ostatnich
    return len - (rand(1, Math.min(3, len)));
  }
  return len - 1;
}

/** generuje instancję z gwarancją istnienia rozwiązania */
function genInstance(opts) {
  const { variant } = opts ?? {};
  const pool = (variant === "ext") ? EXT_POOL : BASIC_POOL;

  let tries = 0;
  while (tries++ < 60) {
    const divisor = weightedPick(pool);

    // długości: zawsze ≥4; specjalne rozkłady dla 4 i 8
    const len = pickLength(divisor);

    const digits = makeDigits(len);
    const holeIdx = pickHoleIndex(divisor, len);

    // w miejsce dziury na chwilę 0 (i tak nadpisywane przy sprawdzaniu)
    const withHole = [...digits];
    withHole[holeIdx] = 0;

    const solutions = solveDigitsFor(divisor, withHole, holeIdx);
    if (solutions.length > 0) {
      return { divisor, digits: withHole, holeIdx, solutions };
    }
  }
  // fallback — nie powinno wystąpić
  const divisor = 3, digits = makeDigits(4), holeIdx = 1;
  return { divisor, digits, holeIdx, solutions: [0, 3, 6, 9] };
}

// ===== generatory =====
export function generateTask() {
  const inst = genInstance({ variant: "basic" });
  return { type: "DivisibilityFillDigitTask", data: { ...inst, variant: "basic" } };
}

export function generateTaskWith(overrides = {}) {
  const inst = genInstance(overrides);
  return { type: "DivisibilityFillDigitTask", data: { ...inst, variant: overrides.variant ?? "basic" } };
}

// ===== render =====
export function renderTask({ data }) {
  const latexNum = buildNumberLatex(data.digits, data.holeIdx);
  return (
    <div style={{ marginBottom: 16 }}>
      <span>
        Uzupełnij cyfrę <InlineMath math={'\\square'} /> w liczbie{" "}
        <InlineMath math={latexNum} /> tak, aby liczba była podzielna przez{" "}
        <InlineMath math={String(data.divisor)} />.
      </span>
    </div>
  );
}

// ===== input =====
export function renderInput({ data }, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? {};
  const set = (val) => onChange?.({ ...(ua || {}), set: val });

  return (
    <div>
      <FiniteSetInput
        value={ua.set}
        onChange={set}
        disabled={disabled}
        placeholder={'{ 0, 5 }'}
        ariaLabel='zbiór cyfr uzupełniających'
        showPreview
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const parsed = (userAnswer && typeof userAnswer === "object")
    ? (userAnswer.set?.parsed ?? parseFiniteSetInput(userAnswer.set))
    : parseFiniteSetInput(userAnswer);

  if (parsed === undefined || parsed === null) return false;

  const exp = data.solutions;
  const eq = (A, B) => Array.isArray(A) && A.length === B.length && A.every((x, i) => x === B[i]);
  return eq(parsed, exp);
}

// ===== odpowiedzi =====
export function renderAnswer({ data }) {
  return <InlineMath math={`\\{ ${data.solutions.join(',\\,')} \\}`} />;
}

export function renderUserAnswer(userAnswer) {
  const parsed = (userAnswer && typeof userAnswer === "object")
    ? (userAnswer.set?.parsed ?? parseFiniteSetInput(userAnswer.set))
    : parseFiniteSetInput(userAnswer);

  if (parsed === undefined) return <i>(brak odpowiedzi)</i>;
  if (parsed === null) return <i>(niepoprawny format)</i>;
  return <InlineMath math={finiteSetToLatex(parsed)} />;
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const latexNum = buildNumberLatex(data.digits, data.holeIdx);
  const sols = `\\{ ${data.solutions.join(',\\,')} \\}`;

  const rule = (() => {
    switch (data.divisor) {
      case 2:  return 'ostatnia cyfra musi być parzysta';
      case 3:  return 'suma cyfr podzielna przez 3';
      case 4:  return 'dwie ostatnie cyfry tworzą liczbę podzielną przez 4';
      case 5:  return 'ostatnia cyfra to 0 lub 5';
      case 6:  return 'jednocześnie podzielna przez 2 i 3';
      case 8:  return 'trzy ostatnie cyfry tworzą liczbę podzielną przez 8';
      case 9:  return 'suma cyfr podzielna przez 9';
      case 10: return 'ostatnia cyfra to 0';
      default: return '';
    }
  })();

  return (
    <div style={{ display: 'grid', rowGap: 8 }}>
      <p><b>Krok 1:</b> Zasada podzielności dla <InlineMath math={String(data.divisor)} />: {rule}.</p>
      <p><b>Krok 2:</b> Sprawdź cyfry <InlineMath math={'0,1,\\ldots,9'} />, które można wstawić w miejsce <InlineMath math={'\\square'} /> liczby <InlineMath math={latexNum} /> i które spełniają warunek.</p>
      <p><b>Wynik:</b> <InlineMath math={sols} />.</p>
    </div>
  );
}
